/* ============================================================================
   JuiceBox cart gifts — reconciler
   Keeps the cart-value gift lines (Genussbox at 70 €, Sorten-Sticker at 100 € since 2026-09-22,
   … whatever shop.metafields.juicebox.cart_gifts says) in step with the cart:
     tier earned and gift absent   ⇒ add it, quantity 1, property _jb_gift
     tier not earned, gift present ⇒ remove it
     gift present twice / qty ≠ 1  ⇒ one line, quantity 1
     gift of a retired tier        ⇒ remove it
   The gift products carry their REAL price; the JuiceBox Cart Gifts cart
   transform prices the line 0,00 once the same rule says it is earned. The
   theme therefore never leaves an un-earned gift in the cart.

   Windows (active_from / active_to: whole days or full hours, shop time) are
   re-evaluated on every run, so a campaign that starts or ends while the page
   stays open is honoured on the next cart action.

   Eligible subtotal, identical to the function: sum of original_line_price
   over every non-gift line (pre-discount; a bundle parent already carries its
   expanded total here), compared against threshold_cents × Shopify.currency.rate.

   Structure copied from jb-adv-bonus-sync.js (external asset because the drawer
   re-renders via innerHTML). Timing is NOT: this one acts on the cart JSON that
   arrives with `cart:change`, writes at once, asks the write for the drawer's
   section HTML (`sections`, the id comes from the drawer itself via
   `cart:prepare-bundled-sections`) and hands that HTML to the drawer through a
   `cart:change` flagged `variant:add`, which Impact's CartDrawer paints with no
   delay. One round trip, no `cart:refresh` fetch. The drawer's own repaint of a
   quantity change lands 1250 ms after that change with the OLDER HTML, into the
   element it captured back then — that element is swapped for a fresh copy, so
   the stale paint lands nowhere (and the fresh HTML is painted once more just
   after, as insurance). Every write re-enters here through the event it
   dispatches, finds nothing to do, and stops. Never touches _adv_bonus lines.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__jbCartGiftsBound) return;
  window.__jbCartGiftsBound = true;

  var script = document.currentScript;
  if (!script) return;
  var cfg;
  try {
    cfg = JSON.parse(script.getAttribute('data-jb-gifts') || '{}');
  } catch (err) {
    console.warn('[jb-cart-gifts] config unreadable — doing nothing', err);
    return;
  }
  if (!cfg || cfg.mode === 'dormant') return;

  var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var HEADERS = { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
  var REAPPLY_AFTER = 1260;     // Impact's CartDrawer repaints a non-add change at 1250 ms; re-paint ours right after
  var FAILED_ADD_TTL = 60000;   // ms before an add that 422'd is retried
  var WRITES_PER_EVENT = 3;     // one user change legitimately needs 1 write, 2 when a duplicate goes first
  var LOOP_WRITES = 12;         // hard stop: more writes than this within LOOP_WINDOW ms = something is fighting us
  var LOOP_WINDOW = 10000;

  // Declared before allTiers below, which calls boundKey() at load time (a later `var` would
  // still be undefined there and the whole reconciler would die on the first tier).
  var BOUND = /^(\d{4}-\d{2}-\d{2})(?: (\d{2}):00)?$/;
  var allTiers = (cfg.mode === 'active' && Array.isArray(cfg.tiers) ? cfg.tiers : [])
    .filter(function (t) {
      return t && Number.isInteger(t.threshold_cents) && t.threshold_cents > 0 && Number.isInteger(t.variant_id) && t.variant_id > 0;
    })
    .map(function (t) {
      // Same window rule as the function: "YYYY-MM-DD" = whole days, "YYYY-MM-DD HH:00" = full
      // hours in shop time; start inclusive, end sharp; anything else makes the tier malformed.
      var from = boundKey(t.active_from, 'from');
      var to = boundKey(t.active_to, 'to');
      if (from === undefined || to === undefined) {
        console.warn('[jb-cart-gifts] tier ' + t.variant_id + ' has a malformed active_from/active_to — ignoring that tier');
        return null;
      }
      return { threshold_cents: t.threshold_cents, variant_id: t.variant_id, from: from, to: to };
    })
    .filter(Boolean)
    .sort(function (a, b) { return a.threshold_cents - b.threshold_cents; });
  var knownGift = {};
  (cfg.all_gift_variant_ids || []).forEach(function (id) { knownGift[id] = true; });
  var hidden = {};
  (cfg.hidden_variant_ids || []).forEach(function (id) { hidden[id] = true; });

  var inFlight = false;
  var rerun = false;
  var pendingTimer = null;
  var failedAdd = {};
  var writeTimes = [];
  var paused = false;

  function boundKey(v, side) {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v !== 'string') return undefined;
    var m = BOUND.exec(v.trim());
    if (!m) return undefined;
    if (m[2] === undefined) return m[1] + ' ' + (side === 'from' ? '00' : '24');
    if (Number(m[2]) > 23) return undefined;
    return m[1] + ' ' + m[2];
  }

  function nowKey() {
    // "YYYY-MM-DD HH" in shop time; the server-rendered key is the fallback.
    try {
      var parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: cfg.timezone || 'Europe/Vienna', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: false
      }).formatToParts(new Date());
      var p = {};
      parts.forEach(function (x) { p[x.type] = x.value; });
      var hh = p.hour === '24' ? '00' : p.hour;
      if (p.year && p.month && p.day && hh) return p.year + '-' + p.month + '-' + p.day + ' ' + hh;
    } catch (err) { /* fall through */ }
    return cfg.now_key || '';
  }

  function activeTiers() {
    var now = nowKey();
    return allTiers.filter(function (t) {
      if (t.from === null && t.to === null) return true;
      if (!now) return false;
      if (t.from !== null && now < t.from) return false;
      if (t.to !== null && !(now < t.to)) return false;
      return true;
    });
  }

  function rate() {
    var r = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    return r > 0 ? r : 1;
  }

  function taggedIds() {
    // Variant ids of cart lines whose PRODUCT is tagged cart-gift — rendered by
    // sections/cart-drawer.liquid, because /cart.js does not expose tags.
    var el = document.querySelector('[data-jb-gift-tagged]');
    var map = {};
    if (!el) return map;
    try {
      JSON.parse(el.textContent || '[]').forEach(function (id) { map[id] = true; });
    } catch (err) { /* ignore a half-rendered blob */ }
    return map;
  }

  function isGiftLine(item, tagged) {
    var props = item.properties || {};
    if (props._adv_bonus != null) return false; // belongs to jb-adv-bonus-sync.js
    return !!(knownGift[item.variant_id] || tagged[item.variant_id] || props._jb_gift != null || props._added_by_cartbot != null);
  }

  function fetchCart() {
    return fetch(routeRoot + 'cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function plan(cart) {
    var items = (cart && cart.items) || [];
    var tagged = taggedIds();
    var eligible = 0;
    var giftLines = [];
    items.forEach(function (item) {
      if (isGiftLine(item, tagged)) giftLines.push(item);
      else eligible += item.original_line_price || 0;
    });
    var r = rate();
    var updates = {};
    var adds = [];
    var now = Date.now();
    var tiers = activeTiers();
    var tierByVariant = {};
    tiers.forEach(function (t) { tierByVariant[t.variant_id] = t; });

    tiers.forEach(function (tier) {
      var lines = giftLines.filter(function (l) { return l.variant_id === tier.variant_id; });
      var earned = eligible >= Math.round(tier.threshold_cents * r);
      if (!earned) {
        lines.forEach(function (l) { updates[l.key] = 0; });
        return;
      }
      if (!lines.length) {
        if (!(failedAdd[tier.variant_id] && now - failedAdd[tier.variant_id] < FAILED_ADD_TTL)) {
          adds.push({ id: tier.variant_id, quantity: 1, properties: { _jb_gift: String(tier.threshold_cents) } });
        }
        return;
      }
      var keep = lines.filter(function (l) { return l.properties && l.properties._jb_gift != null; })[0] || lines[0];
      lines.forEach(function (l) {
        if (l !== keep) updates[l.key] = 0;
        else if (l.quantity !== 1) updates[l.key] = 1;
      });
    });

    // Gift lines that no active tier explains: last weekend's gift, a CartBot
    // leftover, a tagged retiree. Hidden (test) tiers are left alone.
    giftLines.forEach(function (l) {
      if (tierByVariant[l.variant_id] || hidden[l.variant_id]) return;
      updates[l.key] = 0;
    });

    if (Object.keys(updates).length) return { updates: updates }; // removals before adds — fail closed
    if (adds.length) return { add: adds };
    return null;
  }

  function drawerSectionIds() {
    // Impact's CartDrawer answers this with its own section id (same hook its ProductForm uses).
    var sections = [];
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } }));
    } catch (err) { /* no drawer on this page */ }
    return sections;
  }

  function write(action, sections) {
    var body = action.add ? { items: action.add } : { updates: action.updates };
    if (sections.length) body.sections = sections;
    return fetch(routeRoot + (action.add ? 'cart/add.js' : 'cart/update.js'), {
      method: 'POST',
      headers: HEADERS,
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });
  }

  function mergeAdd(cart, addResponse) {
    if (!cart || !Array.isArray(cart.items) || !addResponse || !Array.isArray(addResponse.items)) return null;
    var added = addResponse.items;
    var addedQty = added.reduce(function (n, i) { return n + (i.quantity || 0); }, 0);
    var merged = {};
    Object.keys(cart).forEach(function (k) { merged[k] = cart[k]; });
    merged.items = added.concat(cart.items.filter(function (i) { return !added.some(function (a) { return a.key === i.key; }); }));
    merged.item_count = (cart.item_count || 0) + addedQty;
    merged.sections = addResponse.sections;
    return merged;
  }

  function drawerSectionId() {
    var drawer = document.querySelector('#cart-drawer');
    var section = drawer && drawer.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  function paintDrawer(cart) {
    // Paint the drawer's lines NOW, synchronously, from the section HTML the write returned —
    // mirroring what CartDrawer._onCartChanged does on its 0 ms timer — and then replace the
    // `.cart-drawer__inner` element with an identical copy. CartDrawer's delayed repaint of the
    // user's own change (1250 ms, OLDER HTML) writes into the element it captured back then,
    // which is now detached: no flash of the older lines. Custom elements re-upgrade on insertion,
    // same as after the theme's own innerHTML repaints.
    try {
      var drawer = document.querySelector('#cart-drawer');
      var id = drawerSectionId();
      var html = cart && cart.sections && id ? cart.sections[id] : null;
      if (!drawer || typeof html !== 'string' || !(cart.item_count > 0)) return false;
      var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-drawer__inner');
      var inner = drawer.querySelector('.cart-drawer__inner');
      if (!fresh || !inner) return false;
      var scrolled = [inner, drawer].filter(function (el) { return el.scrollTop > 0; });
      var scrollTops = scrolled.map(function (el) { return el.scrollTop; });
      inner.innerHTML = fresh.innerHTML;
      var copy = inner.cloneNode(true);
      inner.replaceWith(copy);
      scrolled.forEach(function (el, i) { (el === inner ? copy : el).scrollTop = scrollTops[i]; });
      return true;
    } catch (err) {
      return false;
    }
  }

  function dispatchCartChange(cart) {
    // `variant:add` is the flag CartDrawer paints without its 1250 ms delay; the drawer only
    // OPENS on the separate `variant:add` event, which this does not dispatch.
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart, jbCartGifts: true } }));
  }

  var writesSinceEvent = 0;

  function loopDetected() {
    var now = Date.now();
    writeTimes = writeTimes.filter(function (t) { return now - t < LOOP_WINDOW; });
    writeTimes.push(now);
    writesSinceEvent++;
    return writesSinceEvent > WRITES_PER_EVENT || writeTimes.length > LOOP_WRITES;
  }

  var eventSeq = 0;
  var lastEventAt = 0;
  var lastEventStale = false; // did the triggering cart:change leave a delayed repaint pending?

  function afterWrite(cart, sections, seq) {
    var painted = false;
    if (cart && cart.sections && sections.length && sections.every(function (id) { return typeof cart.sections[id] === 'string'; })) {
      var paintedLines = paintDrawer(cart); // lines: now, and the theme's pending stale repaint is aimed at a detached node
      dispatchCartChange(cart);             // footer, cart-count, other listeners; re-enters here and finds nothing to do
      painted = true;
      if (lastEventStale && paintedLines) {
        // Insurance for a theme version whose delayed repaint captures the element late: paint ours again after it.
        // Not for an empty cart: the theme then replaced the whole drawer (the stale target is gone with it), and
        // re-sending the event would only replay its fade-to-empty animation.
        var delay = Math.max(0, lastEventAt + REAPPLY_AFTER - Date.now());
        setTimeout(function () {
          if (eventSeq === seq) { paintDrawer(cart); dispatchCartChange(cart); }
        }, delay);
      }
    }
    if (!painted) {
      // No drawer HTML came back (no drawer on this page): fall back to the full server re-render.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
    var settings = window.themeVariables && window.themeVariables.settings;
    if (settings && settings.pageType === 'cart') {
      // The cart page table is server-rendered (theme.js reloads it the same way).
      var key = 'jbCartGiftsReloads';
      var stamp = Date.now();
      var recent = [];
      try { recent = JSON.parse(sessionStorage.getItem(key) || '[]').filter(function (t) { return stamp - t < 10000; }); } catch (err) { /* ignore */ }
      if (recent.length < 2) {
        recent.push(stamp);
        try { sessionStorage.setItem(key, JSON.stringify(recent)); } catch (err) { /* ignore */ }
        window.location.reload();
      }
    }
  }

  async function reconcile(cart) {
    if (paused) return;
    if (inFlight) {
      rerun = true;
      return;
    }
    inFlight = true;
    try {
      if (!cart) cart = await fetchCart();
      var action = plan(cart);
      if (action) {
        if (loopDetected()) {
          paused = true;
          console.warn('[jb-cart-gifts] too many cart writes in a row — something else is changing the gift lines; pausing until the next real cart change');
          return;
        }
        var seq = eventSeq;
        var sections = drawerSectionIds();
        var res = await write(action, sections);
        if (!res.ok) {
          var body = await res.json().catch(function () { return {}; });
          console.warn('[jb-cart-gifts] write refused', res.status, body.description || body.message || '');
          if (action.add) {
            action.add.forEach(function (a) { failedAdd[a.id] = Date.now(); });
          }
          return; // nothing changed — no repaint
        }
        var updated = await res.json().catch(function () { return null; });
        if (action.add && updated) {
          // add.js answers with the ADDED lines only (its `sections` HTML is the whole drawer, though).
          // Everything downstream — the drawer's item_count, cart-count, this reconciler re-entering —
          // needs the full cart. Build it from the cart we planned on plus the added lines instead of
          // paying a second round trip; totals other than item_count are not read by anyone downstream.
          updated = mergeAdd(cart, updated) || (await fetchCart().then(function (full) { full.sections = updated.sections; return full; }).catch(function () { return null; }));
        }
        if (!updated || !Array.isArray(updated.items)) {
          updated = null;
        }
        afterWrite(updated, sections, seq);
      }
    } catch (err) {
      console.error('[jb-cart-gifts]', err);
    } finally {
      inFlight = false;
      if (rerun) {
        rerun = false;
        reconcile();
      }
    }
  }

  function schedule(cart, delay) {
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(function () {
      pendingTimer = null;
      reconcile(cart);
    }, delay);
  }

  document.addEventListener('cart:change', function (event) {
    var detail = event.detail || {};
    var cart = detail.cart && detail.cart.items ? detail.cart : null;
    if (detail.jbCartGifts) {
      // Our own repaint event: re-enter to converge, but it is not a user change.
      schedule(cart, 0);
      return;
    }
    if (detail.baseEvent) paused = false; // a real cart action lifts the loop breaker
    writesSinceEvent = 0;
    eventSeq++;
    lastEventAt = Date.now();
    // Anything but a variant:add leaves the drawer's 1250 ms repaint with this event's HTML pending.
    lastEventStale = detail.baseEvent !== 'variant:add';
    // The event carries the cart as the server just returned it: plan on that, no delay, no refetch.
    schedule(cart, 0);
  });

  document.addEventListener('cart:refresh', function () {
    // Someone re-rendered the drawer from the server: that is fresher than any re-apply we still owe.
    eventSeq++;
    lastEventStale = false;
    schedule(null, 50);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); });
  } else {
    reconcile();
  }
})();
